import './App.css'
import { useState, useEffect, useRef, useMemo } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import SparkleParticles from './SparkleParticles' // Sparkle effect wrapper
import HappyGhast from './HappyGhast' // Happy Ghast floating animation component
import PerformanceMonitor from './PerformanceMonitor' // Performance monitoring component

// --- Bouncing Player Heads Component ---
// Helper for random initial positions and velocities
function getRandom(min: number, max: number) {
  return Math.random() * (max - min) + min
}

// PlayerHeadsBouncing: displays bouncing player heads with names
const PlayerHeadsBouncing = ({ players, onCollectNugget }: { players: string[], onCollectNugget?: () => void }) => {
  // Store position and velocity for each player
  const [positions, setPositions] = useState<{ x: number; y: number; vx: number; vy: number; }[]>([])
  // Nuggets: {id, x, startY, endY, popX, done}
  const [nuggets, setNuggets] = useState<{
    id: string,
    x: number,
    startY: number,
    endY: number,
    popX: number,
    done: boolean
  }[]>([])
  // Mining effects: {id, x, y, side}
  const [mines, setMines] = useState<{
    id: string,
    x: number,
    y: number,
    side: 'left'|'right'|'top'|'bottom'
  }[]>([])
  // Animation frame reference for cleanup
  const animationRef = useRef<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  // State for pop animations when collecting nuggets
  const [popAnims, setPopAnims] = useState<{id: string, x: number, y: number}[]>([])
  // State for poof animations when collecting nuggets
  const [poofAnims, setPoofAnims] = useState<{id: string, x: number, y: number}[]>([])
  // Ref for nugget collection sound
  const nuggetAudioRef = useRef<HTMLAudioElement | null>(null)

  // Helper to trigger mining effect and drop nugget
  function triggerMineAndNugget(x: number, y: number, side: 'left'|'right'|'top'|'bottom') {
    // Mining effect
    const mineId = `${Date.now()}-${Math.random()}-${side}`
    setMines(effects => [...effects, { id: mineId, x, y, side }])
    // Remove mining effect after 400ms
    setTimeout(() => setMines(effects => effects.filter(e => e.id !== mineId)), 400)
    // Nugget drop (only if not bottom)
    if (side !== 'bottom') {
      const popX = (Math.random() - 0.5) * 60 // random popout left/right
      setNuggets(nugs => [
        ...nugs,
        {
          id: `${Date.now()}-${Math.random()}`,
          x: x + 28, // center of head
          startY: y + 56, // just below head
          endY: document.documentElement.scrollHeight - 48, // bottom
          popX,
          done: false
        }
      ])
    } else {
      // If bottom, drop nugget from bottom edge
      const popX = (Math.random() - 0.5) * 60
      setNuggets(nugs => [
        ...nugs,
        {
          id: `${Date.now()}-${Math.random()}`,
          x: x + 28,
          startY: y + 56,
          endY: document.documentElement.scrollHeight - 48,
          popX,
          done: false
        }
      ])
    }
  }

  // Initialize positions and velocities when players change
  useEffect(() => {
    setPositions(
      players.map(() => ({
        x: getRandom(0, window.innerWidth - 64),
        y: getRandom(0, window.innerHeight - 100),
        vx: getRandom(1.2, 2.5) * (Math.random() > 0.5 ? 1 : -1),
        vy: getRandom(1.2, 2.5) * (Math.random() > 0.5 ? 1 : -1),
      }))
    )
    setNuggets([]) // Clear nuggets on player list change
    setMines([])
    // eslint-disable-next-line
  }, [players.join(",")])

  // Animate heads bouncing off edges and trigger mining/nugget
  useEffect(() => {
    if (players.length === 0) return
    let running = true
    function animate() {
      if (!running) return
      setPositions(prev => prev.map((pos) => {
        let { x, y, vx, vy } = pos
        const size = 56
        const labelHeight = 22
        // Use the full scrollable document size for boundaries
        const w = document.documentElement.scrollWidth
        const h = document.documentElement.scrollHeight
        let hit = null as null | 'left'|'right'|'top'|'bottom'
        // Bounce off left/right
        if (x + vx < 0) { vx = -vx; hit = 'left' }
        else if (x + vx > w - size) { vx = -vx; hit = 'right' }
        // Bounce off top/bottom (account for label)
        if (y + vy < 0) { vy = -vy; hit = 'top' }
        else if (y + vy > h - size - labelHeight) { vy = -vy; hit = 'bottom' }
        if (hit) triggerMineAndNugget(x, y, hit)
        return { x: x + vx, y: y + vy, vx, vy }
      }))
      animationRef.current = requestAnimationFrame(animate)
    }
    animationRef.current = requestAnimationFrame(animate)
    return () => {
      running = false
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [players.length])

  // Re-adjust on window resize
  useEffect(() => {
    const handleResize = () => {
      // Use the full scrollable document size for boundaries
      const w = document.documentElement.scrollWidth
      const h = document.documentElement.scrollHeight
      setPositions(prev => prev.map(pos => ({
        ...pos,
        x: Math.min(pos.x, w - 56),
        y: Math.min(pos.y, h - 78),
      })))
    }
    window.addEventListener('resize', handleResize)
    window.addEventListener('scroll', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('scroll', handleResize)
    }
  }, [])

  // Animate nuggets falling to the bottom
  useEffect(() => {
    if (nuggets.length === 0) return
    let running = true
    function animateNuggets() {
      if (!running) return
      setNuggets(nugs => nugs.map(nug => {
        if (nug.done) return nug
        // Animate Y position
        const speed = 7 + Math.random() * 2
        let newY = nug.startY + speed
        if (newY >= nug.endY) {
          newY = nug.endY
          return { ...nug, startY: newY, done: true }
        }
        return { ...nug, startY: newY }
      }))
      animationRef.current = requestAnimationFrame(animateNuggets)
    }
    animationRef.current = requestAnimationFrame(animateNuggets)
    return () => { running = false }
  }, [nuggets.length])

  if (players.length === 0) return null
  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: document.documentElement.scrollWidth,
        height: document.documentElement.scrollHeight,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      {/* Render mining effects */}
      {mines.map(mine => (
        <span
          key={mine.id}
          style={{
            position: 'absolute',
            left: mine.x + 24,
            top: mine.y + 24,
            pointerEvents: 'none',
            zIndex: 10,
            animation: 'mining-pop 0.4s',
          }}
        >
          {/* Simple mining effect: spark */}
          <svg width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="7" fill="#ffe066" stroke="#b88953" strokeWidth="2" opacity="0.8" />
            <polygon points="16,6 18,16 16,26 14,16" fill="#f7d51d" />
          </svg>
        </span>
      ))}
      {/* Render nuggets */}
      {nuggets.map(nug => (
        <img
          key={nug.id}
          src="/nugget.png"
          alt="Gold nugget"
          style={{
            position: 'absolute',
            left: nug.x + nug.popX,
            top: nug.startY,
            width: 32,
            height: 32,
            pointerEvents: nug.done ? 'auto' : 'none', // Only clickable when done
            zIndex: 5,
            transition: nug.done ? 'top 0.2s' : undefined,
            filter: 'drop-shadow(0 2px 8px #b8895344)',
            animation: nug.done ? undefined : 'nugget-pop 0.7s',
            cursor: nug.done ? 'pointer' : undefined,
            opacity: nug.done ? 1 : 0.95,
          }}
          onClick={() => {
            if (nug.done && onCollectNugget) {
              // Play nugget collection sound
              if (nuggetAudioRef.current) {
                nuggetAudioRef.current.currentTime = 0;
                nuggetAudioRef.current.play();
              }
              onCollectNugget()
              // Show pop animation at this location
              setPopAnims(anims => [...anims, { id: nug.id, x: nug.x + nug.popX, y: nug.startY }])
              setTimeout(() => setPopAnims(anims => anims.filter(a => a.id !== nug.id)), 900)
              // Show poof animation at this location
              setPoofAnims(anims => [...anims, { id: nug.id, x: nug.x + nug.popX, y: nug.startY }])
              setTimeout(() => setPoofAnims(anims => anims.filter(a => a.id !== nug.id)), 700)
              // Remove the nugget from state
              setNuggets(nugs => nugs.filter(n => n.id !== nug.id))
            }
          }}
        />
      ))}
      {/* Render pop animations for collected nuggets */}
      {popAnims.map(anim => (
        <span
          key={anim.id}
          style={{
            position: 'absolute',
            left: anim.x + 8,
            top: anim.y - 18,
            color: '#f7d51d',
            fontWeight: 900,
            fontSize: 22,
            textShadow: '0 2px 8px #b88953, 0 0 8px #fffbe6',
            pointerEvents: 'none',
            zIndex: 10,
            animation: 'nugget-collect-pop 0.9s',
            userSelect: 'none',
          }}
        >
          +1
        </span>
      ))}
      {/* Render poof animations for collected nuggets */}
      {poofAnims.map(anim => (
        <span
          key={anim.id}
          style={{
            position: 'absolute',
            left: anim.x + 2,
            top: anim.y + 6,
            pointerEvents: 'none',
            zIndex: 9,
            animation: 'nugget-poof 0.7s',
            userSelect: 'none',
          }}
        >
          {/* SVG burst/poof */}
          <svg width="32" height="32" viewBox="0 0 32 32">
            <g>
              <circle cx="16" cy="16" r="8" fill="#fffbe6" opacity="0.7" />
              <circle cx="16" cy="16" r="5" fill="#f7d51d" opacity="0.7" />
              <circle cx="8" cy="16" r="2.5" fill="#ffe066" opacity="0.6" />
              <circle cx="24" cy="16" r="2.5" fill="#ffe066" opacity="0.6" />
              <circle cx="16" cy="8" r="2" fill="#ffe066" opacity="0.5" />
              <circle cx="16" cy="24" r="2" fill="#ffe066" opacity="0.5" />
            </g>
          </svg>
        </span>
      ))}
      {/* Nugget collect pop and poof animation keyframes */}
      <style>{`
        @keyframes nugget-collect-pop {
          0% { opacity: 0; transform: translateY(10px) scale(0.7); }
          20% { opacity: 1; transform: translateY(-6px) scale(1.1); }
          80% { opacity: 1; transform: translateY(-18px) scale(1); }
          100% { opacity: 0; transform: translateY(-32px) scale(0.9); }
        }
        @keyframes nugget-poof {
          0% { opacity: 0.7; transform: scale(0.5); }
          40% { opacity: 1; transform: scale(1.1); }
          80% { opacity: 0.8; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.3); }
        }
      `}</style>
      {/* Hidden audio element for nugget collection sound */}
      <audio ref={nuggetAudioRef} src="/nugget.mp3" preload="auto" />
      {/* Render player heads */}
      {players.map((name, idx) => (
        <div key={name}
          style={{
            position: 'absolute',
            left: positions[idx]?.x ?? 0,
            top: positions[idx]?.y ?? 0,
            width: 56,
            textAlign: 'center',
            transition: 'filter 0.2s',
            filter: 'drop-shadow(0 2px 8px #0008)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {/* Minotar player head */}
          <img
            src={`https://minotar.net/avatar/${name}/48`}
            alt={name}
            width={48}
            height={48}
            style={{ borderRadius: 8, background: '#fffbe6', border: '2.5px solid #b88953', display: 'block', margin: '0 auto' }}
            draggable={false}
          />
          {/* Name label */}
          <div style={{
            fontSize: 13,
            color: '#fff',
            background: '#b88953',
            borderRadius: 6,
            marginTop: 2,
            padding: '1px 7px',
            fontWeight: 700,
            boxShadow: '0 1px 4px #0006',
            border: '1.5px solid #f7d51d',
            textShadow: '0 1px 2px #000a',
            maxWidth: 80,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            marginLeft: 'auto',
            marginRight: 'auto',
            pointerEvents: 'none',
          }}>{name}</div>
        </div>
      ))}
    </div>
  )
}

// --- Animated Minecraft Clouds Component ---
const CLOUD_SVGS = [
  // Simple Minecraft-style cloud SVGs (rectangles with rounded corners)
  // You can replace or add more for variety
  (
    <svg width="80" height="32" viewBox="0 0 80 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="12" width="64" height="16" rx="8" fill="#fff"/>
      <rect x="20" y="4" width="32" height="16" rx="8" fill="#fff"/>
    </svg>
  ),
  (
    <svg width="60" height="24" viewBox="0 0 60 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="8" width="60" height="12" rx="6" fill="#fff"/>
      <rect x="12" y="0" width="24" height="12" rx="6" fill="#fff"/>
    </svg>
  ),
  (
    <svg width="48" height="18" viewBox="0 0 48 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="6" width="48" height="12" rx="6" fill="#fff"/>
      <rect x="8" y="0" width="16" height="12" rx="6" fill="#fff"/>
    </svg>
  ),
]

// Helper to get a random number in a range
function rand(min: number, max: number) {
  return Math.random() * (max - min) + min
}

const MinecraftClouds = () => {
  // Memoize cloud configs so they don't change on every render
  const clouds = useMemo(() => {
    return Array.from({ length: 5 }).map((_, i) => {
      const svgIdx = Math.floor(rand(0, CLOUD_SVGS.length))
      const startPercent = rand(-0.2, 0.8)
      return {
        id: i,
        svg: CLOUD_SVGS[svgIdx],
        top: rand(10, 80),
        scale: rand(0.8, 1.3),
        duration: rand(38, 60),
        delay: 0,
        startPercent,
      }
    })
  }, [])

  // State for opacity based on scroll
  const [opacity, setOpacity] = useState(1)
  // State to trigger re-render on scroll/resize
  const [, setTick] = useState(0)
  // Refs for each cloud div
  const cloudRefs = useRef<(HTMLDivElement|null)[]>([])

  // Update on scroll/resize to get new cloud positions
  useEffect(() => {
    const update = () => setTick(t => t + 1)
    window.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      const fade = Math.max(0, 1 - y / 300)
      setOpacity(fade)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'visible',
        opacity,
        transition: 'opacity 0.2s',
      }}
    >
      {clouds.map((cloud, idx) => {
        return (
          <div
            key={cloud.id}
            ref={el => { cloudRefs.current[idx] = el; }}
            style={{
              position: 'absolute',
              top: cloud.top,
              left: `calc(${cloud.startPercent * 100}vw)`,
              transform: `scale(${cloud.scale})`,
              opacity: 0.92,
              filter: 'drop-shadow(0 2px 8px #b8895322)',
              animation: `cloud-move-${cloud.id} ${cloud.duration}s linear 0s infinite`,
              willChange: 'transform',
            }}
          >
            {cloud.svg}
            <style>{`
              @keyframes cloud-move-${cloud.id} {
                0% { left: calc(${cloud.startPercent * 100}vw); }
                100% { left: 100vw; }
              }
            `}</style>
          </div>
        )
      })}
    </div>
  )
}

// --- Minecraft Land with Cacti Component ---
const MinecraftLand = () => {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '100vw',
        height: 110,
        pointerEvents: 'none',
        zIndex: 1, // Behind most content, above background
        overflow: 'visible',
      }}
    >
      {/* Desert mountains background (decorative, behind land/cacti) */}
      <svg width="100%" height="90" viewBox="0 0 1920 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, bottom: 20, zIndex: 0 }}>
        {/* Layered mountain shapes for depth */}
        <path d="M0 80 L200 40 L350 70 L500 30 L700 60 L900 20 L1100 55 L1300 35 L1500 65 L1700 25 L1920 60 L1920 90 L0 90 Z" fill="#e2b97f" />
        <path d="M0 70 L180 35 L320 60 L480 20 L650 50 L850 10 L1050 45 L1250 25 L1450 55 L1650 15 L1920 50 L1920 90 L0 90 Z" fill="#c99c6a" opacity="0.7" />
        <path d="M0 60 L160 30 L300 50 L460 15 L600 40 L800 5 L1000 35 L1200 15 L1400 45 L1600 10 L1920 40 L1920 90 L0 90 Z" fill="#a87c4f" opacity="0.5" />
      </svg>
      {/* Land strip with western town silhouette */}
      <svg width="100%" height="110" viewBox="0 0 1920 110" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, bottom: 0 }}>
        {/* Western town silhouette (buildings, saloon, water tower, etc.) */}
        <g>
          {/* Saloon */}
          <rect x="320" y="30" width="70" height="40" fill="#b88953" stroke="#a07a4b" strokeWidth="3" />
          <rect x="335" y="50" width="40" height="20" fill="#fffbe6" stroke="#a07a4b" strokeWidth="2" />
          <rect x="350" y="60" width="10" height="10" fill="#e6c98a" stroke="#a07a4b" strokeWidth="1.5" />
          <rect x="370" y="60" width="10" height="10" fill="#e6c98a" stroke="#a07a4b" strokeWidth="1.5" />
          <text x="355" y="48" fontSize="10" fill="#fff" fontWeight="bold" fontFamily="monospace">SALOON</text>
          {/* Water tower */}
          <rect x="420" y="20" width="18" height="30" fill="#a07a4b" stroke="#7a5a2c" strokeWidth="2" />
          <rect x="425" y="10" width="8" height="12" fill="#b88953" stroke="#7a5a2c" strokeWidth="1.5" />
          <rect x="423" y="50" width="14" height="8" fill="#7a5a2c" />
          {/* House */}
          <rect x="500" y="45" width="38" height="25" fill="#e6c98a" stroke="#b88953" strokeWidth="2" />
          <rect x="510" y="55" width="10" height="15" fill="#fffbe6" stroke="#b88953" strokeWidth="1.5" />
          <polygon points="500,45 519,30 538,45" fill="#b88953" />
          {/* Barn */}
          <rect x="600" y="40" width="50" height="35" fill="#b88953" stroke="#a07a4b" strokeWidth="2" />
          <rect x="615" y="55" width="20" height="20" fill="#fffbe6" stroke="#a07a4b" strokeWidth="1.5" />
          <polygon points="600,40 625,25 650,40" fill="#a07a4b" />
          {/* Church */}
          <rect x="700" y="35" width="30" height="40" fill="#e6c98a" stroke="#b88953" strokeWidth="2" />
          <rect x="710" y="55" width="10" height="20" fill="#fffbe6" stroke="#b88953" strokeWidth="1.5" />
          <polygon points="700,35 715,20 730,35" fill="#b88953" />
          <rect x="715" y="20" width="4" height="10" fill="#fffbe6" />
        </g>
        {/* Land and bumpy effect */}
        <rect x="0" y="60" width="1920" height="50" fill="#e6c98a" />
        <rect x="0" y="90" width="1920" height="20" fill="#b88953" />
        <rect x="0" y="105" width="1920" height="5" fill="#a07a4b" />
        {/* Bumpy land effect */}
        <ellipse cx="200" cy="80" rx="60" ry="18" fill="#f7e7be" />
        <ellipse cx="600" cy="95" rx="80" ry="22" fill="#f7e7be" />
        <ellipse cx="1200" cy="85" rx="70" ry="20" fill="#f7e7be" />
        <ellipse cx="1700" cy="100" rx="90" ry="25" fill="#f7e7be" />
      </svg>
      {/* Cacti SVGs, positioned at various spots */}
      <div style={{ position: 'absolute', left: 80, bottom: 18 }}>
        <svg width="38" height="70" viewBox="0 0 38 70" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="14" y="20" width="10" height="50" rx="5" fill="#3e9c35" />
          <rect x="6" y="40" width="8" height="18" rx="4" fill="#3e9c35" />
          <rect x="24" y="35" width="8" height="15" rx="4" fill="#3e9c35" />
          <rect x="17" y="10" width="4" height="18" rx="2" fill="#3e9c35" />
        </svg>
      </div>
      <div style={{ position: 'absolute', left: 320, bottom: 22 }}>
        <svg width="28" height="50" viewBox="0 0 28 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="15" width="8" height="35" rx="4" fill="#3e9c35" />
          <rect x="3" y="30" width="6" height="12" rx="3" fill="#3e9c35" />
          <rect x="19" y="28" width="6" height="10" rx="3" fill="#3e9c35" />
        </svg>
      </div>
      <div style={{ position: 'absolute', left: '70vw', bottom: 20 }}>
        <svg width="34" height="60" viewBox="0 0 34 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="12" y="18" width="10" height="42" rx="5" fill="#3e9c35" />
          <rect x="4" y="36" width="7" height="15" rx="3.5" fill="#3e9c35" />
          <rect x="23" y="32" width="7" height="13" rx="3.5" fill="#3e9c35" />
        </svg>
      </div>
      <div style={{ position: 'absolute', left: '90vw', bottom: 25 }}>
        <svg width="22" height="38" viewBox="0 0 22 38" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="7" y="10" width="8" height="28" rx="4" fill="#3e9c35" />
          <rect x="2" y="25" width="5" height="8" rx="2.5" fill="#3e9c35" />
        </svg>
      </div>
      {/* Animated tornado on the right side */}
      <div style={{ position: 'absolute', right: 60, bottom: 10, zIndex: 2, pointerEvents: 'none', width: 54, height: 90 }}>
        {/* Tornado SVG with swirling animation */}
        <div className="tornado-anim tornado-move" style={{ width: 54, height: 90, position: 'absolute', left: 0, top: 0 }}>
          <svg width="54" height="90" viewBox="0 0 54 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
            <g style={{ filter: 'blur(0.5px)' }}>
              <ellipse cx="27" cy="18" rx="22" ry="10" fill="#b3c6d9" opacity="0.7" />
              <ellipse cx="27" cy="32" rx="18" ry="8" fill="#b3c6d9" opacity="0.6" />
              <ellipse cx="27" cy="48" rx="13" ry="6" fill="#b3c6d9" opacity="0.5" />
              <ellipse cx="27" cy="62" rx="8" ry="4" fill="#b3c6d9" opacity="0.4" />
              <ellipse cx="27" cy="78" rx="4" ry="2" fill="#b3c6d9" opacity="0.3" />
            </g>
          </svg>
          {/* Animated dust clouds at the base */}
          <svg width="54" height="30" viewBox="0 0 54 30" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', left: 0, top: 70 }}>
            <ellipse className="tornado-dust tornado-dust1" cx="15" cy="18" rx="10" ry="4" fill="#e6c98a" opacity="0.7" />
            <ellipse className="tornado-dust tornado-dust2" cx="27" cy="22" rx="7" ry="3" fill="#b88953" opacity="0.5" />
            <ellipse className="tornado-dust tornado-dust3" cx="39" cy="18" rx="8" ry="3.5" fill="#e6c98a" opacity="0.6" />
          </svg>
        </div>
        <style>{`
          @keyframes tornado-spin {
            0% { transform: rotate(0deg) scaleX(1); }
            40% { transform: rotate(-8deg) scaleX(0.97); }
            60% { transform: rotate(8deg) scaleX(1.03); }
            100% { transform: rotate(0deg) scaleX(1); }
          }
          @keyframes tornado-move {
            0% { transform: translateX(0); }
            20% { transform: translateX(-18px); }
            50% { transform: translateX(0); }
            70% { transform: translateX(12px); }
            100% { transform: translateX(0); }
          }
          .tornado-anim {
            animation: tornado-spin 2.2s infinite cubic-bezier(.4,0,.6,1);
            will-change: transform;
          }
          .tornado-move {
            animation: tornado-move 7s infinite cubic-bezier(.4,0,.6,1);
          }
          .tornado-dust1 {
            animation: dust-move1 2.8s infinite linear;
          }
          .tornado-dust2 {
            animation: dust-move2 3.2s infinite linear;
          }
          .tornado-dust3 {
            animation: dust-move3 2.5s infinite linear;
          }
          @keyframes dust-move1 {
            0% { opacity: 0.7; transform: translateX(0) scale(1); }
            50% { opacity: 0.3; transform: translateX(-10px) scale(1.15); }
            100% { opacity: 0.7; transform: translateX(0) scale(1); }
          }
          @keyframes dust-move2 {
            0% { opacity: 0.5; transform: translateX(0) scale(1); }
            50% { opacity: 0.2; transform: translateX(8px) scale(1.1); }
            100% { opacity: 0.5; transform: translateX(0) scale(1); }
          }
          @keyframes dust-move3 {
            0% { opacity: 0.6; transform: translateX(0) scale(1); }
            50% { opacity: 0.2; transform: translateX(12px) scale(1.18); }
            100% { opacity: 0.6; transform: translateX(0) scale(1); }
          }
        `}</style>
      </div>
    </div>
  )
}

// Helper to fade in sections on scroll
function useSectionFadeIn() {
  const refs = useRef<(HTMLElement | null)[]>([])
  useEffect(() => {
    const handleScroll = () => {
      refs.current.forEach((el) => {
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect.top < window.innerHeight - 60) {
          el.classList.add('visible')
        }
      })
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  return refs
}

function MainSite() {
  // State for copy button feedback
  const [copied, setCopied] = useState(false)
  const [ctaCopied, setCtaCopied] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const serverIP = 'play.goldrushmc.com'

  // Music player state
  const [musicPlaying, setMusicPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Scroll to top button state
  const [showScrollTop, setShowScrollTop] = useState(false)
  // Show nugget button at bottom left only when user is at bottom
  const [showNuggetBtn, setShowNuggetBtn] = useState(false)
  useEffect(() => {
    const checkBottom = () => {
      setShowScrollTop(window.scrollY > 200)
      // Add a buffer to the bottom check for reliability
      const buffer = 40
      const scrollBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - buffer
      setShowNuggetBtn(scrollBottom)
    }
    window.addEventListener('scroll', checkBottom)
    window.addEventListener('resize', checkBottom)
    checkBottom()
    return () => {
      window.removeEventListener('scroll', checkBottom)
      window.removeEventListener('resize', checkBottom)
    }
  }, [])

  // Handle play/pause music (no autoplay, only after user click)
  const handleMusicToggle = () => {
    if (!audioRef.current) return
    if (musicPlaying) {
      audioRef.current.pause()
      setMusicPlaying(false)
    } else {
      audioRef.current.currentTime = 0
      audioRef.current.play()
      setMusicPlaying(true)
    }
  }

  // Gallery carousel state
  const galleryImages = [
    '/merchant.png',
    '/armadillo.png',
    '/town.png',
    '/gunsmith.png',
    '/Priest.png',
    '/missionhawk.png',
    // Custom webm videos placed in public folder
    '/tntstick.webm',
    '/horse.webm',
  ]
  // Fun captions for each gallery image
  const galleryCaptions = [
    "Goods for all your needs.",
    "A wild armadillo appears.",
    "Bustling frontier town.",
    "Visit the local gunsmith.",
    "Sins are forgiven.",
    "This bird talks too much.",
    "TNT Stick: Explosive fun!",
    "Horseback riding in style!"
  ]
  const [galleryIndex, setGalleryIndex] = useState(0)

  // Auto-rotate gallery every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setGalleryIndex((prev) => (prev + 1) % galleryImages.length)
    }, 30000)
    return () => clearInterval(interval)
  }, [galleryImages.length])

  // Get indices for left, center, right images
  const getIndices = () => {
    const len = galleryImages.length
    const center = galleryIndex
    const left = (center - 1 + len) % len
    const right = (center + 1) % len
    return [left, center, right]
  }
  const [leftIdx, centerIdx, rightIdx] = getIndices()

  // Click/tap side images to switch
  const handleSideClick = (idx: number) => setGalleryIndex(idx)

  // Basic swipe support
  const touchStartX = useRef<number | null>(null)
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    if (deltaX > 40) setGalleryIndex((galleryIndex - 1 + galleryImages.length) % galleryImages.length)
    else if (deltaX < -40) setGalleryIndex((galleryIndex + 1) % galleryImages.length)
    touchStartX.current = null
  }

  // Helper to render image or webm video with caption overlay
  const renderGalleryMedia = (src: string, className: string, alt: string, style: any, onClick?: () => void, caption?: string) => {
    if (src.endsWith('.webm')) {
      return (
        <span className="gallery-image-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
        <video
          src={src}
          className={className}
          style={style}
          autoPlay
          loop
          muted
          playsInline
          onClick={onClick}
          draggable={false}
        />
          {/* Caption overlay */}
          {caption && <span className="gallery-image-caption">{caption}</span>}
        </span>
      )
    }
    return (
      <span className="gallery-image-wrapper" style={{ position: 'relative', display: 'inline-block' }}>
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        onClick={onClick}
        draggable={false}
      />
        {/* Caption overlay */}
        {caption && <span className="gallery-image-caption">{caption}</span>}
      </span>
    )
  }

  // Copy server IP to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(serverIP)
    setCopied(true)
    setTimeout(() => setCopied(false), 1200)
  }

  // Copy server IP from CTA button (no scroll)
  const handleCtaCopy = (e: React.MouseEvent) => {
    e.preventDefault()
    navigator.clipboard.writeText(serverIP)
    setCtaCopied(true)
    setTimeout(() => setCtaCopied(false), 1200)
  }

  // Scroll to top
  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Dropdown nav menu links (now using React Router)
  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Map', href: '#' },
    { label: 'Store', href: 'https://store.goldrushmc.com', external: true },
  ]

  // --- Server status state ---
  const [serverStatus, setServerStatus] = useState<'online' | 'offline' | 'loading'>('loading')
  const [playerNames, setPlayerNames] = useState<string[]>([])

  // Fetch server status and player names from mcsrvstat.us (auto-refresh every 5s)
  useEffect(() => {
    setServerStatus('loading')
    const fetchStatus = () => {
      fetch(`https://api.mcsrvstat.us/2/${serverIP}`)
        .then(res => res.json())
        .then(data => {
          if (data && data.online) {
            setServerStatus('online')
            setPlayerNames(data.players?.list ?? [])
          } else {
            setServerStatus('offline')
            setPlayerNames([])
          }
        })
        .catch(() => {
          setServerStatus('offline')
          setPlayerNames([])
        })
    }
    fetchStatus() // Fetch immediately on mount
    // Poll every 5 seconds (was 30 seconds)
    const interval = setInterval(fetchStatus, 5000)
    return () => clearInterval(interval)
  }, [serverIP])

  // Close nav menu when clicking outside
  useEffect(() => {
    if (!navOpen) return
    function handleClick(e: MouseEvent) {
      // Only close if click is outside nav button or dropdown
      const navBtn = document.querySelector('.nav-btn')
      const navDropdown = document.querySelector('.nav-dropdown')
      if (
        navBtn && navDropdown &&
        !navBtn.contains(e.target as Node) &&
        !navDropdown.contains(e.target as Node)
      ) {
        setNavOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [navOpen])

  // Section fade-in refs
  const sectionRefs = useSectionFadeIn()

  // --- Parallax state for clouds and cacti ---
  // We'll use a state to trigger re-render, but update DOM directly for performance
  useEffect(() => {
    function handleParallax() {
      const scrollY = window.scrollY
      // Parallax for clouds (background pseudo-elements)
      const root = document.getElementById('root')
      if (root) {
        root.style.setProperty('--cloud-parallax', `${scrollY * 0.18}px`)
        root.style.setProperty('--cloud-parallax2', `${scrollY * 0.12}px`)
      }
      // Parallax for cacti
      const cactusLeft = document.querySelector('.cactus.left') as HTMLElement
      const cactusRight = document.querySelector('.cactus.right') as HTMLElement
      if (cactusLeft) cactusLeft.style.transform = `translateY(${-scrollY * 0.08}px)`
      if (cactusRight) cactusRight.style.transform = `translateY(${-scrollY * 0.13}px)`
    }
    window.addEventListener('scroll', handleParallax)
    handleParallax()
    return () => window.removeEventListener('scroll', handleParallax)
  }, [])

  // --- Animated emoji in section header ---
  const [cactusAnim, setCactusAnim] = useState(false)
  useEffect(() => {
    const interval = setInterval(() => {
      setCactusAnim(true)
      setTimeout(() => setCactusAnim(false), 900)
    }, 3200)
    return () => clearInterval(interval)
  }, [])

  // Set low volume on mount
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.15 // Set low volume
    }
  }, [])

  // Try to play music on first user interaction (for mobile autoplay compatibility)
  useEffect(() => {
    const tryPlay = () => {
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play();
      }
      window.removeEventListener('touchstart', tryPlay);
      window.removeEventListener('mousedown', tryPlay);
    };
    window.addEventListener('touchstart', tryPlay, { once: true });
    window.addEventListener('mousedown', tryPlay, { once: true });
    return () => {
      window.removeEventListener('touchstart', tryPlay);
      window.removeEventListener('mousedown', tryPlay);
    };
  }, []);

  // Nugget collection state
  const [nuggetCount, setNuggetCount] = useState(0)
  // Handler to collect a nugget (called from PlayerHeadsBouncing)
  const handleCollectNugget = () => {
    setNuggetCount(count => count + 1)
  }

  return (
    <div className="western-bg">
      {/* Performance monitoring */}
      <PerformanceMonitor />
      {/* Animated Minecraft-style clouds at the top */}
      <MinecraftClouds />
      {/* Happy Ghast floating animation */}
      <HappyGhast />
      {/* Western music player (minimal icon button) */}
      <audio
        ref={audioRef}
        src="/music.mp3" // Local music file in public/
        loop
        autoPlay // Play by default
        // No autoplay, only play after user click
      />
      {/* Floating button stack: music, scroll-to-top, donate (bottom right) */}
      <div className="floating-btn-stack right">
        {/* Scroll-to-top button above music button */}
        {showScrollTop && (
          <SparkleParticles count={3} size={5}>
          <button
            className="scroll-top-btn minimal"
            onClick={handleScrollTop}
            aria-label="Scroll to top"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="13" fill="#f7d51d" stroke="#b88953" strokeWidth="2" />
              <path d="M14 20V8" stroke="#b88953" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M9 13l5-5 5 5" stroke="#b88953" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          </SparkleParticles>
        )}
        <SparkleParticles count={3} size={5}>
        <button
          className="music-btn minimal"
          onClick={handleMusicToggle}
          aria-label={musicPlaying ? 'Pause western music' : 'Play western music'}
        >
          {musicPlaying ? (
            // Pause icon SVG
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="4" width="4" height="16" rx="2" fill="#b88953" />
              <rect x="15" y="4" width="4" height="16" rx="2" fill="#b88953" />
            </svg>
          ) : (
            // Music note icon SVG
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 17V5l12-2v12" stroke="#b88953" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="6" cy="18" r="3" fill="#b88953" />
              <circle cx="18" cy="16" r="3" fill="#b88953" />
            </svg>
          )}
        </button>
        </SparkleParticles>
        <SparkleParticles count={3} size={6}>
        <a
          href="https://www.paypal.com/ncp/payment/S6KY4ALAYP5RW"
          className="donate-btn minimal"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Donate via PayPal"
        >
          <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="19" cy="19" r="17" fill="#f7d51d" stroke="#b88953" strokeWidth="2.5" />
            <path d="M19 29c-4.5-3.5-9-6.5-9-11A5 5 0 0119 14a5 5 0 019 4c0 4.5-4.5 7.5-9 11z" fill="#e25555" stroke="#b88953" strokeWidth="2" />
          </svg>
        </a>
        </SparkleParticles>
      </div>
      {/* Floating nugget button (bottom left, only at bottom) */}
      {showNuggetBtn && (
        <button
          className="nugget-btn minimal"
          style={{ position: 'fixed', left: 24, bottom: 90, zIndex: 1000, width: 48, height: 48, background: '#fffbe6', border: '2px solid #b88953', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px #b8895344', cursor: 'pointer', transition: 'background 0.2s, box-shadow 0.2s, border 0.2s, opacity 0.2s', opacity: 0.95 }}
          aria-label="Gold Nugget Counter"
          onClick={() => { /* No animation/message needed */ }}
        >
          <img src="/nugget.png" alt="Gold Nugget" width={32} height={32} style={{ display: 'block' }} />
          {/* Nugget counter badge */}
          <span style={{
            position: 'absolute',
            right: -8,
            top: -8,
            background: '#f7d51d',
            color: '#b88953',
            border: '2px solid #b88953',
            borderRadius: '50%',
            minWidth: 22,
            height: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 15,
            boxShadow: '0 2px 8px #b8895344',
            zIndex: 2,
            pointerEvents: 'none',
          }}>{nuggetCount}</span>
        </button>
      )}
      {/* Navigation button (top left, menu icon) */}
      <div style={{ position: 'fixed', top: 12, left: 12, zIndex: 1000 }}>
        {/* Add sparkles around nav button */}
        <SparkleParticles count={3} size={5}>
        <button
          className="nav-btn minimal small"
          aria-label="Open navigation menu"
          style={{ width: 36, height: 36 }}
          onClick={() => setNavOpen((open) => !open)}
        >
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="15" fill="#f7d51d" stroke="#b88953" strokeWidth="2.5" />
            <rect x="9" y="11" width="14" height="2.5" rx="1.2" fill="#b88953" />
            <rect x="9" y="15" width="14" height="2.5" rx="1.2" fill="#b88953" />
            <rect x="9" y="19" width="14" height="2.5" rx="1.2" fill="#b88953" />
          </svg>
        </button>
        </SparkleParticles>
        {navOpen && (
          <div className="nav-dropdown small">
            {navLinks.map((link) => (
              link.href.startsWith('/') ? (
                <Link key={link.label} to={link.href} className="nav-dropdown-link small" onClick={() => setNavOpen(false)}>{link.label}</Link>
              ) : (
                <a key={link.label} href={link.href} className="nav-dropdown-link small" onClick={() => setNavOpen(false)} target="_blank" rel="noopener noreferrer">{link.label}</a>
              )
            ))}
          </div>
        )}
      </div>

      {/* Decorative cacti for wild west vibe */}
      <div className="cactus left" aria-hidden="true"></div>
      <div className="cactus right" aria-hidden="true"></div>

      {/* Header Section */}
      <header className="western-header">
        <h1 className="western-title main-title">Gold Rush MC</h1>
        <p className="western-subtitle">🤠 Yeehaw! Welcome to the wildest Minecraft server in the West! 🧱</p>
        <button
          className="cta-btn"
          onClick={handleCtaCopy}
          aria-label="Saddle Up & Join (copies server IP)"
          style={{ position: 'relative' }}
        >
          🏇 Saddle Up & Join!
          {ctaCopied && (
            <span className="copy-feedback">Copied!</span>
          )}
        </button>
      </header>

      {/* About Section */}
      <section className="about-section" ref={el => { sectionRefs.current[0] = el }} style={{ position: 'relative' }}>
        <TorchFlicker />
        <GoldNuggetsRain />
        <h2>
          What is Gold Rush MC?{' '}
          <span className={`cactus-emoji${cactusAnim ? ' cactus-emoji-anim' : ''}`} role="img" aria-label="cactus">🌵</span>
        </h2>
        <p>
          🌵 Gold Rush MC is a rootin'-tootin' Minecraft server where you can build blocky forts, hunt for treasure, and ride horses with your posse! Make friends, explore the wild, and show off your wild west style!
        </p>
      </section>

      {/* Bouncing Player Heads Overlay */}
      <PlayerHeadsBouncing players={serverStatus === 'online' ? playerNames : []} onCollectNugget={handleCollectNugget} />

      {/* Gallery: 3-image carousel, larger, clickable/swipeable, webm support, underlap on mobile */}
      <section className="gallery-section" id="gallery" ref={el => { sectionRefs.current[1] = el }} style={{ position: 'relative' }}>
        <TorchFlicker />
        <GoldNuggetsRain />
        <h2>Gallery</h2>
        <div
          className="gallery-carousel"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {renderGalleryMedia(
            galleryImages[leftIdx],
            'gallery-image gallery-image-side gallery-image-side-clickable',
            'Gallery preview left',
            { zIndex: 1, cursor: 'pointer' },
            () => handleSideClick(leftIdx),
            undefined
          )}
          {renderGalleryMedia(
            galleryImages[centerIdx],
            'gallery-image gallery-image-center',
            'Gallery main',
            { zIndex: 2 },
            undefined,
            galleryCaptions[centerIdx]
          )}
          {renderGalleryMedia(
            galleryImages[rightIdx],
            'gallery-image gallery-image-side gallery-image-side-clickable',
            'Gallery preview right',
            { zIndex: 1, cursor: 'pointer' },
            () => handleSideClick(rightIdx),
            undefined
          )}
        </div>
        <div className="gallery-indicator">
          {galleryImages.map((_, i) => (
            <span
              key={i}
              className={`gallery-indicator-number${i === centerIdx ? ' active' : ''}`}
              onClick={() => setGalleryIndex(i)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              tabIndex={0}
              aria-label={`Go to image ${i + 1}`}
              role="button"
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setGalleryIndex(i) }}
            >
              {i + 1}
            </span>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" ref={el => { sectionRefs.current[2] = el }} style={{ position: 'relative' }}>
        <TorchFlicker />
        <GoldNuggetsRain />
        <h2>Why You'll Love It</h2>
        <ul>
          <li>🤝 Form an outfit with your friends</li>
          <li>🏜️ Claim land</li>
          <li>🐎 Race horses</li>
          <li>💰 Find gold & secret loot</li>
          <li>🎯 Play minigames & wild events</li>
        </ul>
        <div style={{ marginTop: 12 }}>
          <a href="https://www.minecraft.net/" target="_blank" rel="noopener noreferrer" className="external-link">Learn more about Minecraft</a>
        </div>
        <div style={{ marginTop: 18 }}>
          <a href="#gallery" className="see-gallery-btn">🏜️ See the gallery</a>
        </div>
      </section>

      {/* Contact/Join Section */}
      <section className="contact-section" id="join" ref={el => { sectionRefs.current[3] = el }} style={{ position: 'relative' }}>
        <TorchFlicker />
        <GoldNuggetsRain />
        <h2>Ready to Ride?</h2>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                display: 'inline-block',
                width: 13,
                height: 13,
                borderRadius: '50%',
                background:
                  serverStatus === 'loading'
                    ? 'linear-gradient(90deg,#bbb 50%,#eee 100%)'
                    : serverStatus === 'online'
                    ? '#3ec46d'
                    : '#e25555',
                border: '2px solid #fff',
                boxShadow: '0 1px 4px #0002',
                marginRight: 4,
                transition: 'background 0.2s',
              }}
              title={
                serverStatus === 'loading'
                  ? 'Checking server status...'
                  : serverStatus === 'online'
                  ? 'Server is online'
                  : 'Server is offline'
              }
            />
            IP:
            <a 
              href="minecraft://play.goldrushmc.com"
              style={{ color: '#222', textDecoration: 'underline', fontWeight: 700, cursor: 'pointer', wordBreak: 'break-all' }}
              title="Click to join in Minecraft"
            >
              {serverIP}
            </a>
            <button
              onClick={handleCopy}
              className="clipboard-btn minimal"
              aria-label="Copy server IP"
            >
              {copied ? (
                // Checkmark SVG
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="10" cy="10" r="10" fill="#222" />
                  <path d="M6 10.5L9 13.5L14 8.5" stroke="#f7d51d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                // Modern clipboard SVG icon
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5.5" y="4.5" width="11" height="13" rx="2" fill="#fffbe6" stroke="#b88953" strokeWidth="2" />
                  <rect x="8" y="2.5" width="6" height="3" rx="1.2" fill="#f7d51d" stroke="#b88953" strokeWidth="1.5" />
                  <rect x="7.5" y="7.5" width="7" height="1.5" rx="0.75" fill="#b88953" />
                  <rect x="7.5" y="10.5" width="7" height="1.5" rx="0.75" fill="#b88953" />
                  <rect x="7.5" y="13.5" width="7" height="1.5" rx="0.75" fill="#b88953" />
                </svg>
              )}
            </button>
          </span>
        </div>
      </section>

      {/* Minecraft land with cacti at the bottom, now after contact section */}
      <MinecraftLand />

      {/* Footer */}
      <footer className="western-footer" style={{ marginBottom: 120 }}>
        {/* Extra bottom margin so the MinecraftLand does not block footer content */}
        <p>Yeehaw! See you on the prairie!<br />
        <span style={{ fontSize: '0.95em', color: '#b88953', display: 'block', marginTop: '0.5em' }}>&copy; 2025 Gold Rush MC</span><br />
        Contact: <a href="mailto:koevet@goldrushmc.com" style={{ color: '#b88953', textDecoration: 'underline' }}>koevet@goldrushmc.com</a></p>
      </footer>
      {/* Site version label, small and subtle for reference */}
      <div className="site-version">Version: v1.0.0</div>
    </div>
  )
}

// GoldNuggetsRain: animates gold nuggets falling inside a section box
interface Nugget {
  id: string;
  left: number;
  top: number;
  speed: number;
}
const GoldNuggetsRain = () => {
  const [nuggets, setNuggets] = useState<Nugget[]>([])
  const boxRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let running = true
    let animationId: number | null = null
    let intervalId: number | null = null
    
    // Helper to spawn a nugget
    function spawnNugget() {
      if (!boxRef.current || !running) return
      const box = boxRef.current.getBoundingClientRect()
      const left = Math.random() * (box.width - 32)
      setNuggets(nugs => [
        ...nugs,
        {
          id: Math.random().toString(36).slice(2),
          left,
          top: -32,
          speed: 1.2 + Math.random() * 1.8,
        },
      ])
    }
    
    // Animate nuggets falling
    function animate() {
      if (!running) return
      setNuggets(nugs => nugs.map(nug => ({
        ...nug,
        top: nug.top + nug.speed,
      })).filter(nug => nug.top < (boxRef.current?.offsetHeight || 400)))
      animationId = requestAnimationFrame(animate)
    }
    
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - pause animations
        running = false
        if (animationId) {
          cancelAnimationFrame(animationId)
          animationId = null
        }
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      } else {
        // Page is visible - resume animations
        running = true
        // Clear existing nuggets to prevent buildup
        setNuggets([])
        // Restart spawning and animation
        intervalId = setInterval(spawnNugget, 2500)
        animate()
      }
    }
    
    // Start animations
    intervalId = setInterval(spawnNugget, 2500)
    animate()
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      running = false
      if (animationId) cancelAnimationFrame(animationId)
      if (intervalId) clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return (
    <div ref={boxRef} style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}>
      {nuggets.map(nug => (
        <img
          key={nug.id}
          src="/nugget.png"
          alt="Gold nugget"
          style={{
            position: 'absolute',
            left: nug.left,
            top: nug.top,
            width: 32,
            height: 32,
            opacity: 0.92,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 8px #b8895344)',
            userSelect: 'none',
          }}
          draggable={false}
        />
      ))}
    </div>
  )
}

// TorchFlicker: Animated Minecraft-style torch with flickering flame
const TorchFlicker = () => (
  <span style={{
    position: 'absolute',
    top: 10,
    right: 18,
    zIndex: 3,
    width: 32,
    height: 48,
    pointerEvents: 'none',
    userSelect: 'none',
    display: 'block',
  }}>
    <svg width="32" height="48" viewBox="0 0 32 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Torch stick */}
      <rect x="13" y="22" width="6" height="22" rx="2.5" fill="#a07a4b" stroke="#7a5a2c" strokeWidth="2" />
      {/* Torch head */}
      <rect x="11" y="16" width="10" height="8" rx="2" fill="#b88953" stroke="#a07a4b" strokeWidth="1.5" />
      {/* Flickering flame (animated with CSS) */}
      <g className="torch-flame">
        <ellipse cx="16" cy="14" rx="5" ry="7" fill="#ffe066" />
        <ellipse cx="16" cy="13" rx="3" ry="5" fill="#f7d51d" />
        <ellipse cx="16" cy="12" rx="1.5" ry="3" fill="#fffbe6" />
      </g>
    </svg>
  </span>
)

// App component: sets up routes for main site and store
function App() {
  // Scroll to top on route change for better UX
  const location = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  return (
    <Routes>
      {/* Main site at root */}
      <Route path="/" element={<MainSite />} />
      {/* (Optional: add 404 page here) */}
    </Routes>
  )
}

export default App
